import React, { useCallback, useEffect, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  ControlRoomDrawer,
  DataTable,
  DrawerBackdrop,
  DrawerHeader,
  EmptyStateBox,
  MetricCard,
  MetricLabel,
  MetricStrip,
  MetricSubtext,
  MetricValue,
  StatusPill,
  TableShell,
  Timeline,
  TimelineItem,
  WorkspaceButton,
  type Tone,
} from './shared/CreditWorkspace';

// Loan-detail Activity timeline drawer. Triggered from a row in
// LeveragedLoansScreen (kind='LSTA') or the PC screens (kind='PC').
//
// R5-F10: for LSTA loans the PRIMARY feed is the canonical tranche-event API
// (AXP-130 parent/child model): facility-level parent events with per-portfolio
// child applications, distinct statuses (REVIEW / FAILED / SKIPPED_STALE /
// PARTIALLY_APPLIED / PENDING_EFFECTIVE) and a Retry affordance for FAILED /
// EXCEPTION children plus Apply for due PENDING_EFFECTIVE parents. The legacy
// per-loan lifecycle feed is kept as a secondary "Legacy activity" section.
// PC loans keep the legacy private-credit lifecycle feed only (no tranche
// events exist for bilateral PC).

interface LifecycleEventRow {
  id: number;
  eventType: string;
  eventDate: string;
  eventAmount?: number | null;
  baseRate?: number | null;
  currency?: string | null;
  status?: string | null;
  message?: string | null;
  sourceJobId?: string | null;
  sourceDocumentName?: string | null;
  sourceDocumentType?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  portfolioId?: number | null;
  portfolioCode?: string | null;
}

// Canonical parent event — ll_tranche_event (LLTrancheEventController).
interface TrancheEventRow {
  id: number;
  eventType: string;
  eventDate?: string | null;
  effectiveDate?: string | null;
  entitlementDate?: string | null;
  facilityAmount?: number | null;
  globalOutstandingBefore?: number | null;
  globalOutstandingAfter?: number | null;
  interestPeriodStart?: string | null;
  interestPeriodEnd?: string | null;
  sourceDocumentName?: string | null;
  sourceJobId?: string | null;
  status?: string | null;
  message?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
}

// Canonical child application — ll_tranche_event_application.
interface TrancheChildRow {
  id: number;
  eventId: number;
  portfolioId: number;
  preFunded?: number | null;
  postFunded?: number | null;
  appliedPct?: number | null;
  appliedAmount?: number | null;
  calculatedAmount?: number | null;
  statedAmount?: number | null;
  varianceAmount?: number | null;
  oidReliefComponent?: number | null;
  tradeId?: number | null;
  status?: string | null;
  message?: string | null;
}

const EVENT_TONE: Record<string, Tone> = {
  DRAWDOWN: 'cyan',
  PAYDOWN: 'violet',
  RATE_RESET: 'amber',
  AMENDMENT: 'violet',
  INTEREST: 'emerald',
  INTEREST_PAYMENT: 'emerald',
};

// R5-F10: distinct rendering per canonical status — a stale reset must never
// read as APPLIED, a partial failure must never read as fully applied.
const STATUS_TONE: Record<string, Tone> = {
  APPLIED: 'emerald',
  PARTIALLY_APPLIED: 'amber',
  FAILED: 'red',
  REVIEW: 'amber',
  SKIPPED_STALE: 'slate',
  PENDING_EFFECTIVE: 'cyan',
  PENDING: 'slate',
  EXCEPTION: 'amber',
  SKIPPED: 'slate',
};

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <StatusPill $tone={STATUS_TONE[status || ''] || 'slate'}>{(status || '--').replace(/_/g, ' ')}</StatusPill>
);

const fmtCurrency = (n?: number | null, ccy = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(n)) return '--';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${Number(n).toLocaleString()}`;
  }
};
const fmtRate = (n?: number | null) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '--';
  // Loan lifecycle event rates use the platform's canonical percent scale:
  // 8.45 means 8.45%, and 0.50 means 0.50% (not 50%).
  return `${Number(n).toFixed(3)}%`;
};
const parseDisplayDate = (s: string) => {
  // `new Date('YYYY-MM-DD')` is UTC and renders the prior calendar date in
  // American time zones. Date-only business fields must stay date-only.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(s);
};
const fmtDate = (s?: string | null) => (s ? parseDisplayDate(s).toLocaleDateString() : '--');
const fmtDateTime = (s?: string | null) => (s ? new Date(s).toLocaleString() : '--');
const todayISO = () => getTodayDate();

const SectionTitle: React.FC<{ children: React.ReactNode; description?: string }> = ({ children, description }) => (
  <div style={{ margin: '1.2rem 0 0.65rem' }}>
    <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '0.95rem' }}>{children}</h3>
    {description && <div style={{ color: '#64748b', fontSize: '0.74rem', marginTop: '0.25rem' }}>{description}</div>}
  </div>
);

const ProvenanceLine: React.FC<{ createdBy?: string | null; createdAt?: string | null; documentName?: string | null; jobId?: string | null }> = ({
  createdBy, createdAt, documentName, jobId,
}) => (
  <div style={{ color: '#64748b', fontSize: '0.7rem', marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
    <span>by {createdBy || 'SYSTEM'}</span>
    <span>{fmtDateTime(createdAt)}</span>
    {documentName && <span>doc {documentName}</span>}
    {jobId && <span>job {String(jobId).slice(0, 8)}...</span>}
  </div>
);

interface Props {
  /**
   * LSTA leveraged loan path: pass `loanId` (the dim_leveraged_loan PK).
   * Canonical: /api/credit/ll/tranche-events/tranches/{loanId}/events
   * Legacy (secondary): /api/credit/loans/{loanId}/lifecycle-events
   */
  loanId?: number;
  /**
   * Private credit path (unified IBOR): pass `securityId` from the
   * fact_positions row. Backend joins dim_private_credit_loan by security_id
   * to resolve the silo PK. Frontend never handles pc_loan_id.
   * Endpoint: /api/private-credit/by-security/{securityId}/lifecycle-events
   */
  securityId?: number;
  /**
   * Which ledger to read. Defaults to 'LSTA' for backwards-compat with the
   * existing LeveragedLoansScreen call site.
   */
  kind?: 'LSTA' | 'PC';
  loanName?: string;
  onClose: () => void;
}

const LoanActivityDrawer: React.FC<Props> = ({ loanId, securityId, loanName, onClose, kind = 'LSTA' }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<LifecycleEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Canonical tranche-event state (LSTA only).
  const [trancheEvents, setTrancheEvents] = useState<TrancheEventRow[]>([]);
  const [childrenByEvent, setChildrenByEvent] = useState<Record<number, TrancheChildRow[]>>({});
  const [portfolioNames, setPortfolioNames] = useState<Record<number, string>>({});
  const [trancheError, setTrancheError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Retry/Apply are ops actions. The backend enforces roles regardless; the
  // UI gates to ops-ish roles when role context exists, else stays visible.
  const role = (user?.role || '').toUpperCase();
  const canOps = ['ADMIN', 'PLATFORM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN'].includes(role);

  const loadCanonical = useCallback(async () => {
    if (kind !== 'LSTA' || loanId == null) return;
    try {
      setTrancheError(null);
      const resp = await axios.get(`/api/credit/ll/tranche-events/tranches/${loanId}/events`);
      const parents: TrancheEventRow[] = Array.isArray(resp.data) ? resp.data : [];
      setTrancheEvents(parents);
      const withChildren = await Promise.all(parents.map(async p => {
        try {
          const kids = await axios.get(`/api/credit/ll/tranche-events/events/${p.id}/applications`);
          return [p.id, Array.isArray(kids.data) ? kids.data : []] as [number, TrancheChildRow[]];
        } catch {
          return [p.id, []] as [number, TrancheChildRow[]];
        }
      }));
      setChildrenByEvent(Object.fromEntries(withChildren));
    } catch (e: any) {
      setTrancheError(e?.response?.data?.error || e?.message || 'Failed to load tranche events');
    }
  }, [kind, loanId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        let endpoint: string | null = null;
        if (kind === 'PC') {
          if (securityId == null) {
            setError('No security_id supplied for PC activity lookup');
            setLoading(false);
            return;
          }
          endpoint = `/api/private-credit/by-security/${securityId}/lifecycle-events`;
        } else {
          if (loanId == null) {
            setError('No loanId supplied for LSTA activity lookup');
            setLoading(false);
            return;
          }
          endpoint = `/api/credit/loans/${loanId}/lifecycle-events`;
        }
        const resp = await axios.get(endpoint);
        if (!cancelled) setEvents(Array.isArray(resp.data) ? resp.data : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    if (kind === 'LSTA' && loanId != null) {
      loadCanonical();
      // Portfolio id → code map for child rows (best-effort).
      (async () => {
        try {
          const resp = await axios.get('/api/dimensions/portfolios');
          const list = Array.isArray(resp.data) ? resp.data : (resp.data?.content || []);
          const map: Record<number, string> = {};
          for (const p of list) {
            if (p?.id != null) map[p.id] = p.portfolioCode || p.portfolio_code || p.portfolioName || p.name || `#${p.id}`;
          }
          if (!cancelled) setPortfolioNames(map);
        } catch { /* fall back to #id */ }
      })();
    }
    return () => { cancelled = true; };
  }, [loanId, securityId, kind, loadCanonical]);

  const retryChild = async (applicationId: number) => {
    setActionBusy(`retry-${applicationId}`);
    setActionError(null);
    try {
      await axios.post(`/api/credit/ll/tranche-events/applications/${applicationId}/retry`);
      await loadCanonical();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Retry failed');
    } finally {
      setActionBusy(null);
    }
  };

  const applyParent = async (eventId: number) => {
    setActionBusy(`apply-${eventId}`);
    setActionError(null);
    try {
      await axios.post(`/api/credit/ll/tranche-events/events/${eventId}/apply`);
      await loadCanonical();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Apply failed');
    } finally {
      setActionBusy(null);
    }
  };

  const headerKey = kind === 'PC'
    ? (securityId != null ? `Security #${securityId}` : 'Private Credit Loan')
    : (loanId != null ? `Loan #${loanId}` : 'Loan');
  const portfolioLabel = (id: number) => portfolioNames[id] || `#${id}`;
  const isLsta = kind === 'LSTA';

  const exceptionChildren = Object.values(childrenByEvent).flat()
    .filter(c => c.status === 'FAILED' || c.status === 'EXCEPTION').length;
  const reviewParents = trancheEvents.filter(e => e.status === 'REVIEW' || e.status === 'FAILED').length;
  const applyDueCount = trancheEvents.filter(e =>
    e.status === 'PENDING_EFFECTIVE' && (!e.effectiveDate || e.effectiveDate <= todayISO())).length;
  const attentionCount = exceptionChildren + reviewParents + applyDueCount;
  const latestEventDate = (isLsta && trancheEvents.length > 0
    ? (trancheEvents[0].effectiveDate || trancheEvents[0].eventDate)
    : events[0]?.eventDate) || null;

  const renderTrancheEvent = (ev: TrancheEventRow) => {
    const tone = EVENT_TONE[ev.eventType] || 'slate';
    const kids = childrenByEvent[ev.id] || [];
    const applyDue = ev.status === 'PENDING_EFFECTIVE' && (!ev.effectiveDate || ev.effectiveDate <= todayISO());
    return (
      <TimelineItem key={`te-${ev.id}`} $tone={ev.status === 'FAILED' ? 'red' : tone}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{ev.eventType.replace(/_/g, ' ')}</strong>
            <StatusBadge status={ev.status} />
            {ev.status === 'PENDING_EFFECTIVE' && !applyDue && (
              <span style={{ color: '#64748b', fontSize: '0.68rem' }}>parked until {fmtDate(ev.effectiveDate)}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {canOps && applyDue && (
              <WorkspaceButton $variant="primary" disabled={actionBusy === `apply-${ev.id}`} onClick={() => applyParent(ev.id)}>
                {actionBusy === `apply-${ev.id}` ? 'Applying...' : 'Apply now'}
              </WorkspaceButton>
            )}
            <span style={{ color: '#94a3b8', fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums' }}>
              {fmtDate(ev.effectiveDate || ev.eventDate)}
            </span>
          </div>
        </div>
        <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {ev.facilityAmount != null && <span>Facility {fmtCurrency(ev.facilityAmount)}</span>}
          {ev.globalOutstandingBefore != null && ev.globalOutstandingAfter != null && (
            <span>Global outstanding {fmtCurrency(ev.globalOutstandingBefore)} to {fmtCurrency(ev.globalOutstandingAfter)}</span>
          )}
          {ev.interestPeriodStart && ev.interestPeriodEnd && (
            <span>Period {fmtDate(ev.interestPeriodStart)} - {fmtDate(ev.interestPeriodEnd)}</span>
          )}
        </div>
        {kids.length > 0 && (
          <TableShell style={{ marginTop: 8 }}>
            <div style={{ overflowX: 'auto' }}>
              <DataTable>
                <thead>
                  <tr>
                    <th>Portfolio</th>
                    <th>Applied</th>
                    <th>Calc / Stated</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {kids.map(child => (
                    <React.Fragment key={child.id}>
                      <tr>
                        <td>{portfolioLabel(child.portfolioId)}</td>
                        <td>
                          {fmtCurrency(child.appliedAmount)}
                          {child.oidReliefComponent != null && Number(child.oidReliefComponent) > 0 && (
                            <span style={{ color: '#a78bfa', fontSize: '0.68rem' }}> · OID {fmtCurrency(child.oidReliefComponent)}</span>
                          )}
                        </td>
                        <td>
                          {child.calculatedAmount != null ? fmtCurrency(child.calculatedAmount) : '--'}
                          {child.statedAmount != null && <span style={{ color: '#94a3b8' }}> / {fmtCurrency(child.statedAmount)}</span>}
                        </td>
                        <td><StatusBadge status={child.status} /></td>
                        <td style={{ textAlign: 'right' }}>
                          {canOps && (child.status === 'FAILED' || child.status === 'EXCEPTION') && (
                            <WorkspaceButton disabled={actionBusy === `retry-${child.id}`} onClick={() => retryChild(child.id)}>
                              {actionBusy === `retry-${child.id}` ? 'Retrying...' : 'Retry'}
                            </WorkspaceButton>
                          )}
                        </td>
                      </tr>
                      {child.message && (
                        <tr>
                          <td colSpan={5} style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{child.message}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </DataTable>
            </div>
          </TableShell>
        )}
        {ev.message && (
          <div style={{
            color: '#cbd5e1', fontSize: '0.76rem', marginTop: 8,
            background: 'rgba(2, 6, 23, 0.5)', border: '1px solid rgba(148, 163, 184, 0.1)',
            borderRadius: 8, padding: '0.5rem 0.7rem', wordBreak: 'break-word',
          }}>
            {ev.message}
          </div>
        )}
        <ProvenanceLine createdBy={ev.createdBy} createdAt={ev.createdAt} documentName={ev.sourceDocumentName} jobId={ev.sourceJobId} />
      </TimelineItem>
    );
  };

  const renderLegacyEvent = (ev: LifecycleEventRow) => {
    const tone = EVENT_TONE[ev.eventType] || 'slate';
    return (
      <TimelineItem key={ev.id} $tone={ev.status === 'FAILED' ? 'red' : tone}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <strong>{ev.eventType.replace(/_/g, ' ')}</strong>
            <StatusBadge status={ev.status} />
          </div>
          <span style={{ color: '#94a3b8', fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(ev.eventDate)}</span>
        </div>
        <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {ev.eventAmount !== null && ev.eventAmount !== undefined && (
            <span>{fmtCurrency(ev.eventAmount, ev.currency || 'USD')}</span>
          )}
          {ev.baseRate !== null && ev.baseRate !== undefined && (
            <span>{ev.eventType === 'RATE_RESET' ? 'New base rate' : 'Rate'} {fmtRate(ev.baseRate)}</span>
          )}
          {ev.portfolioCode && <span>{ev.portfolioCode}</span>}
        </div>
        {ev.message && (
          <div style={{
            color: '#cbd5e1', fontSize: '0.76rem', marginTop: 8,
            background: 'rgba(2, 6, 23, 0.5)', border: '1px solid rgba(148, 163, 184, 0.1)',
            borderRadius: 8, padding: '0.5rem 0.7rem', wordBreak: 'break-word',
          }}>
            {ev.message}
          </div>
        )}
        <ProvenanceLine createdBy={ev.createdBy} createdAt={ev.createdAt} documentName={ev.sourceDocumentName} jobId={ev.sourceJobId} />
      </TimelineItem>
    );
  };

  return (
    <>
      <DrawerBackdrop onClick={onClose} />
      <ControlRoomDrawer>
        <DrawerHeader>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#38bdf8', fontSize: 11, fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.16em' }}>
              {kind === 'PC' ? 'Private Credit · Lifecycle' : 'Leveraged Loan · Lifecycle'}
            </div>
            <h2 style={{ margin: '0.25rem 0 0', color: '#f8fafc', fontSize: '1.35rem' }}>
              {loanName || headerKey}
            </h2>
            <div style={{ color: '#94a3b8', marginTop: 4, fontSize: '0.8rem' }}>
              {kind === 'PC'
                ? 'Bilateral private-credit lifecycle events from the unified IBOR'
                : 'Canonical facility events with per-portfolio applications (AXP-130)'}
            </div>
          </div>
          <WorkspaceButton onClick={onClose}>Close</WorkspaceButton>
        </DrawerHeader>

        <MetricStrip>
          <MetricCard $tone="cyan">
            <MetricLabel>Events</MetricLabel>
            <MetricValue>{isLsta ? trancheEvents.length : events.length}</MetricValue>
            <MetricSubtext>{isLsta ? `${events.length} legacy entries below` : 'lifecycle feed'}</MetricSubtext>
          </MetricCard>
          <MetricCard $tone="blue">
            <MetricLabel>Latest Event</MetricLabel>
            <MetricValue>{latestEventDate ? fmtDate(latestEventDate) : '--'}</MetricValue>
            <MetricSubtext>effective date</MetricSubtext>
          </MetricCard>
          <MetricCard $tone={attentionCount === 0 ? 'emerald' : exceptionChildren > 0 ? 'red' : 'amber'}>
            <MetricLabel>Attention</MetricLabel>
            <MetricValue>{attentionCount === 0 ? 'Clear' : attentionCount}</MetricValue>
            <MetricSubtext>
              {attentionCount === 0
                ? 'No failures, reviews or due applies'
                : `${exceptionChildren} failed · ${reviewParents} review · ${applyDueCount} apply due`}
            </MetricSubtext>
          </MetricCard>
        </MetricStrip>

        {actionError && <EmptyStateBox style={{ color: '#fca5a5' }}>{actionError}</EmptyStateBox>}

        {isLsta && (
          <>
            <SectionTitle description="Canonical facility parent events with per-portfolio applications. A stale reset never reads as applied; a partial failure never reads as complete.">
              Borrower Events
            </SectionTitle>
            {trancheError && <EmptyStateBox style={{ color: '#fca5a5' }}>{trancheError}</EmptyStateBox>}
            {!trancheError && trancheEvents.length === 0 && (
              <EmptyStateBox>No borrower events recorded yet for this tranche.</EmptyStateBox>
            )}
            {!trancheError && trancheEvents.length > 0 && (
              <Timeline>{trancheEvents.map(renderTrancheEvent)}</Timeline>
            )}
            <SectionTitle description="Per-loan lifecycle feed (pre-AXP-130).">Legacy Activity</SectionTitle>
          </>
        )}

        {loading && <EmptyStateBox>Loading activity...</EmptyStateBox>}
        {error && <EmptyStateBox style={{ color: '#fca5a5' }}>{error}</EmptyStateBox>}
        {!loading && !error && events.length === 0 && (
          <EmptyStateBox>{isLsta ? 'No legacy lifecycle events recorded for this loan.' : 'No lifecycle events recorded yet for this loan.'}</EmptyStateBox>
        )}
        {!loading && !error && events.length > 0 && (
          <Timeline>{events.map(renderLegacyEvent)}</Timeline>
        )}
      </ControlRoomDrawer>
    </>
  );
};

export default LoanActivityDrawer;
